/**
 * @fileoverview Signup Success Step Component
 * @description Success message and redirect after completing signup
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  VStack,
  Box,
  Icon,
  Heading,
  Text,
  Button,
  Progress,
} from '@chakra-ui/react';
import { MdCheckCircle, MdArrowForward } from 'react-icons/md';

interface SignupSuccessStepProps {
  bankConnected: boolean;
  redirectPath?: string;
  autoRedirectDelay?: number;
}

/**
 * Success step shown after completing signup
 *
 * Shows success message and auto-redirects to the app.
 */
const SignupSuccessStep: React.FC<SignupSuccessStepProps> = ({
  bankConnected,
  redirectPath = '/home',
  autoRedirectDelay = 5,
}) => {
  const navigate = useNavigate();
  const [countdown, setCountdown] = useState(autoRedirectDelay);

  // Countdown and auto-redirect
  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          navigate(redirectPath);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [navigate, redirectPath]);

  const handleContinue = () => {
    navigate(redirectPath);
  };

  return (
    <VStack spacing={8} textAlign="center" py={8}>
      {/* Success Icon */}
      <Box
        p={4}
        borderRadius="full"
        bg="green.50"
        _dark={{ bg: 'green.900' }}
      >
        <Icon as={MdCheckCircle} boxSize={16} color="green.500" />
      </Box>

      {/* Success Message */}
      <VStack spacing={2}>
        <Heading size="lg" color="green.600" _dark={{ color: 'green.400' }}>
          Account Created Successfully!
        </Heading>
        <Text color="gray.600" _dark={{ color: 'gray.400' }} maxW="md">
          {bankConnected
            ? 'Your account and bank connection have been set up. You\'re all ready to go!'
            : 'Your account has been created. You can connect your bank account anytime from your dashboard.'}
        </Text>
      </VStack>

      {/* What's next */}
      <VStack spacing={1} pt={2}>
        <Text fontSize="sm" color="gray.500">
          Redirecting to your dashboard in {countdown} seconds...
        </Text>
        <Progress
          value={(autoRedirectDelay - countdown) / autoRedirectDelay * 100}
          size="xs"
          colorScheme="green"
          w="200px"
          borderRadius="full"
        />
      </VStack>

      {/* Continue Button */}
      <Button
        size="lg"
        colorScheme="green"
        onClick={handleContinue}
        rightIcon={<Icon as={MdArrowForward} />}
      >
        Continue to Dashboard
      </Button>
    </VStack>
  );
};

export default SignupSuccessStep;
